import { HEADER_QUANTITY, toCanonical } from './unitConversion';

// The needle gauge (sizes given as a diameter in mm are left out, never mixed with gauges).
const NEEDLE_SIZE_KEYS = [['diameter', 'diameterUnit'], ['outerDiameter', 'outerDiameterUnit'],
    ['innerDiameter', 'innerDiameterUnit'], ['middleDiameter', 'middleDiameterUnit'], ['size', 'unit']];
const needleGauge = (def) => {
    for (const [key, unitKey] of NEEDLE_SIZE_KEYS) {
        const unit = def[unitKey];
        if (def[key] !== undefined && def[key] !== null && (!unit || /^(g|gauge)$/i.test(unit))) return def[key];
    }
    return undefined;
};

export const STRICT_CONFIG_MASTER = [
    { header: 'Experiment Id', pattern: 'Experiment Id' },
    { header: 'DOI', pattern: 'Research Doi' },
    { header: 'Polymer Name(s)', pattern: 'Polymer Names' },
    { header: 'Polymer Ratio', pattern: 'Polymer Ratios' },
    { header: 'Polymer Type', pattern: 'Polymer Type' },
    { header: 'Solution Preparation', pattern: 'Solution Preparation' },
    { header: 'Additive(s)', pattern: 'Additive Names' },
    { header: 'Solvent Name(s)', pattern: 'Solvent Names' },
    { header: 'Solvent Ratio', pattern: 'Solvent Ratios' },
    { header: 'Sol. Concentration', pattern: 'Solution Concentration' },
    { header: 'Collector Type', pattern: 'Collector Type' },
    { header: 'Needle Type', pattern: 'Needle Type' },
    { header: 'Needle Diameter (G)', pattern: 'Needle Diameter' },
    { header: 'Voltage (kV)', pattern: 'Process Voltage', unitBase: 'Process Voltage' },
    { header: 'Flow Rate (mL/h)', pattern: 'Process Flow Rate', unitBase: 'Process Flow Rate' },
    { header: 'Tip Dist (cm)', pattern: 'Process Tip Collector Distance', unitBase: 'Process Tip Collector Distance' },
    { header: 'Temperature (°C)', pattern: 'Ambient Temperature', unitBase: 'Ambient Temperature' },
    { header: 'Humidity (%)', pattern: 'Ambient Humidity', unitBase: 'Ambient Humidity' },
    { header: 'Formation Stable', pattern: 'Fiber Is Formation Stable' },
    { header: 'Morphology', pattern: 'Morphology Type' },
    { header: 'Instability', pattern: 'Instability Type' },
    { header: 'Fiber Diameter (nm)', pattern: 'Fiber Diameter', unitBase: 'Fiber Diameter' },
    { header: 'Diameter Variation', pattern: 'Fiber Diameter Variation' },
    { header: 'Nanofiber Weight', pattern: 'Fiber Product Weight' },

    // Mechanical Properties
    { header: 'Tensile Strength (MPa)', pattern: 'Mechanical Tensile Strength', unitBase: 'Mechanical Tensile Strength' },
    { header: 'Modulus (MPa)', pattern: 'Mechanical Modulus', unitBase: 'Mechanical Modulus' },
    { header: 'Elongation at Break (%)', pattern: 'Mechanical Elongation At Break', unitBase: 'Mechanical Elongation At Break' },
    { header: 'Fracture Behaviour', pattern: 'Mechanical Fracture Behaviour' },

    // Functional Properties
    { header: 'Surface Area (m²/g)', pattern: 'Functional Surface Area', unitBase: 'Functional Surface Area' },
    { header: 'Porosity (%)', pattern: 'Functional Porosity', unitBase: 'Functional Porosity' },
    { header: 'Permeability', pattern: 'Functional Permeability', unitBase: 'Functional Permeability' },
    { header: 'Wettability (Contact Angle)', pattern: 'Functional Wettability', unitBase: 'Functional Wettability' },
    { header: 'Electrical Cond.', pattern: 'Functional Electrical Conductivity', unitBase: 'Functional Electrical Conductivity' },
    { header: 'Thermal Cond.', pattern: 'Functional Thermal Conductivity', unitBase: 'Functional Thermal Conductivity' }
];

export const STRICT_CONFIG_USER = [
    { header: 'Status', pattern: 'Status' },
    ...STRICT_CONFIG_MASTER
];

export const flattenRecursive = (obj, prefix = '') => {
    const formatKey = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
    const rawFlat = {};

    const recurse = (curr, pfx) => {
        for (const key in curr) {
            const val = curr[key];
            if (key === 'fiberImages') {
                rawFlat['Images'] = val;
                continue;
            }
            if (key === 'userInfo') {
                if (val && val.userId) rawFlat._userId = val.userId;
                continue;
            }

            // Handle Arrays of Objects with 'label' (Instability, Morphology)
            if ((key === 'instability' || key === 'fiberMorphology') && Array.isArray(val)) {
                const labels = val.filter(v => v && v.label).map(v => v.label).join(', ');
                const cleanKey = key === 'instability' ? 'Instability Type' : 'Morphology Type';
                rawFlat[cleanKey] = labels || '-';
                continue;
            }

            const joinKeys = (parent, child) => {
                if (!parent) return child;
                const parentWords = parent.split(' ');
                const lastParentWord = parentWords[parentWords.length - 1];
                if (child.startsWith(lastParentWord) && lastParentWord.length > 2) {
                    return child;
                }
                return `${parent} ${child}`.trim();
            };

            // Array Handling (Polymer/Solvent)
            if ((key === 'polymerComponents' || key === 'solventComponents') && Array.isArray(val)) {
                const names = [];
                const ratios = [];
                val.forEach(item => {
                    if (item.polymerName) names.push(item.polymerName);
                    if (item.solventName) names.push(item.solventName);
                    if (item.weightRatio !== undefined) ratios.push(item.weightRatio);
                    else if (item.percentWeight !== undefined) ratios.push(item.percentWeight);
                });

                const cleanBase = formatKey(key).replace(' Components', '');
                const baseKey = joinKeys(pfx, cleanBase);

                rawFlat[`${baseKey} Names`] = names.join(' + ');
                rawFlat[`${baseKey} Ratios`] = ratios.length > 0 ? ratios.join(' : ') : '-';
                if (key === 'polymerComponents' && val.length > 0) {
                    // Blend = two or more polymers; copolymer = one polymer made of several monomers
                    rawFlat[`${baseKey} Type`] = val.length > 1 ? 'Blend'
                        : (val[0].copolymer || val[0].composition?.length) ? 'Copolymer' : 'Homopolymer';
                }
                continue;
            }

            // How the spun solution was prepared: one solution, separately prepared solutions mixed,
            // or a solution per needle channel (core/shell)
            if (key === 'solutions') {
                const feeds = Array.isArray(val) ? val : [];
                const channels = feeds.some(f => f?.channel && f.channel !== 'MAIN');
                rawFlat['Solution Preparation'] = !feeds.length ? 'Single solution' : channels ? 'Core/shell solutions' : 'Mixed solutions';
                if (channels) rawFlat['Polymer Type'] = 'Core/shell';
                continue;
            }

            // Additives (nanoparticles, salts, drugs, ...): not part of the polymer blend
            if (key === 'additives' && Array.isArray(val)) {
                const names = val.map(a => a?.additiveName).filter(Boolean);
                rawFlat['Additive Names'] = names.length ? names.join(' + ') : 'None';
                continue;
            }

            if (key === 'needleDefinition' && typeof val === 'object' && val !== null) {
                const diameter = needleGauge(val);
                if (diameter !== undefined && diameter !== null) {
                    rawFlat['Needle Diameter'] = diameter;
                }
                if (val.type) {
                    rawFlat['Needle Type'] = val.type;
                }
                continue;
            }

            if (val && typeof val === 'object' && !Array.isArray(val)) {
                const cleanParentKey = formatKey(key).replace(' Property', '').replace(' Parameter', '').replace(' Metadata', '');
                const nextPfx = joinKeys(pfx, cleanParentKey);
                recurse(val, nextPfx);
            } else if (!Array.isArray(val)) {
                const cleanLeafKey = formatKey(key);
                const finalKey = joinKeys(pfx, cleanLeafKey);
                rawFlat[finalKey] = val;
            }
        }
    };
    recurse(obj, prefix);
    if (!prefix && obj && 'polymerProperty' in obj && rawFlat['Solution Preparation'] === undefined) {
        rawFlat['Solution Preparation'] = 'Single solution';
    }
    return rawFlat;
};

export const processDatasetRawData = (rawData, config = STRICT_CONFIG_MASTER) => {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) return [];

    return rawData.map(originalItem => {
        if (!originalItem) return null;
        // If already flattened (check specific key?), skip? 
        // But usually we pass raw data here.
        // Dataset can include mix? No. 
        // If originalItem has 'Experiment Id' (spaces), it is flatten. 
        // If it has 'experimentId' (camel), it is raw.
        let flattenedItem = originalItem;
        if (originalItem.recordId || originalItem.experimentId || originalItem.researchMetadata) {
            flattenedItem = flattenRecursive(originalItem);
        }

        const newRow = {};

        // Process via pattern map. A column labelled with a unit gets its values in that unit,
        // whatever unit they were entered in (an unconvertible one is left empty).
        config.forEach(cfg => {
            const rawKey = Object.keys(flattenedItem).find(k => k === cfg.pattern);
            if (rawKey !== undefined) {
                let val = flattenedItem[rawKey];
                if (typeof val === 'boolean') val = val ? 'Yes' : 'No';
                const quantity = HEADER_QUANTITY[cfg.header];
                if (quantity) {
                    const unitKey = cfg.header === 'Diameter Variation'
                        ? (flattenedItem['Fiber Diameter Variation Unit'] ? 'Fiber Diameter Variation Unit' : 'Fiber Diameter Unit')
                        : `${cfg.unitBase || cfg.pattern} Unit`;
                    val = toCanonical(quantity, val, flattenedItem[unitKey]);
                }
                newRow[cfg.header] = (val !== null && val !== undefined && val !== '') ? val : '-';
            } else {
                newRow[cfg.header] = '-';
            }
        });

        // Preserve special metadata fields
        if (flattenedItem._userId) newRow._userId = flattenedItem._userId;
        if (flattenedItem.Images) newRow.Images = flattenedItem.Images;

        // Preserve needed IDs for Actions
        if (flattenedItem['Record Id']) newRow.recordId = flattenedItem['Record Id'];
        if (flattenedItem['Experiment Id']) newRow['Experiment Id'] = flattenedItem['Experiment Id'];
        if (flattenedItem._originalIndex !== undefined) newRow._originalIndex = flattenedItem._originalIndex;

        return newRow;
    }).filter(row => row !== null);
};

// "Aug 7, 2026" style date for compact dataset metadata lines, matching the
// format already used on the public dataset page.
export const formatDatasetDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};
